'use client'

import { Checkmark24, X24 } from '@vinted/monochrome-icons'
import { Button, Cell, Dialog, Divider, Icon, Image, Navigation, Text } from '@vinted/web-ui'
import { useEffect } from 'react'

import { useTracking } from '@marketplace-web/shared/event-tracker'
import { useTranslate } from '@marketplace-web/shared/i18n'
import { ContentLoader, useAsset } from '@marketplace-web/shared/ui-helpers'

import { kycViewScreenEvent } from '_libs/common/event-tracker/events'
import { Screen } from 'constants/tracking/screens'

import useFetchBankEducation from '../../../../../hooks/useFetchBankEducation'

type Props = {
  show: boolean
  buttonText?: string
  onUpload: () => void
  onClose: () => void
}

const EducationModal = ({ show, buttonText, onUpload, onClose }: Props) => {
  const { track } = useTracking()
  const educationModalTranslate = useTranslate(
    'kyc_form.supporting_documents.bank_statement.education_modal',
  )
  const translate = useTranslate()
  const asset = useAsset('/assets/kyc')

  const { data: requirements, isLoading } = useFetchBankEducation()

  useEffect(() => {
    if (!show) return

    track(kycViewScreenEvent({ screen: Screen.KycBankStatementEducation }))
  }, [show, track])

  const renderSection = ({ title, subtitle }: { title: string; subtitle?: string }) => (
    <Cell
      key={title}
      styling={Cell.Styling.Narrow}
      title={title}
      body={subtitle && <Text as="h3" html text={subtitle} type={Text.Type.Subtitle} />}
      prefix={<Icon name={Checkmark24} />}
    />
  )

  return (
    <Dialog show={show}>
      <Navigation
        right={
          <Button
            styling={Button.Styling.Flat}
            onClick={onClose}
            iconName={X24}
            testId="kyc_bank_statement_education_close"
            inline
            aria={{
              'aria-label': translate(
                'kyc_form.a11y.supporting_documents.bank_statement.close_button',
              ),
            }}
          />
        }
      />
      <div className="modal-contained-content">
        <div className="u-flexbox u-justify-content-center">
          <Image
            role="img"
            src={asset('bank_statement_education.svg', {
              theme: { dark: 'dark/bank_statement_education.svg' },
            })}
            size={Image.Size.X4Large}
            alt=""
          />
        </div>
        <Cell>
          <Text
            as="h1"
            text={educationModalTranslate('title')}
            type={Text.Type.Heading}
            width={Text.Width.Parent}
            alignment={Text.Alignment.Center}
          />
        </Cell>
        <div className="u-ui-padding-horizontal-large u-ui-padding-bottom-large">
          <Text
            as="p"
            text={educationModalTranslate('subtitle')}
            type={Text.Type.Body}
            width={Text.Width.Parent}
            alignment={Text.Alignment.Center}
          />
        </div>
        {isLoading ? <ContentLoader styling={ContentLoader.Styling.Wide} /> : null}
        {requirements?.sections.length ? (
          <>
            <Cell>
              <Text
                as="h2"
                text={educationModalTranslate('must_show')}
                type={Text.Type.Title}
                width={Text.Width.Parent}
              />
            </Cell>
            {requirements?.sections.map(({ title, subtitle }) =>
              renderSection({
                title,
                subtitle,
              }),
            )}
          </>
        ) : null}
      </div>
      <Divider />
      <Cell>
        <Button
          text={buttonText || educationModalTranslate('buttons.upload')}
          styling={Button.Styling.Filled}
          onClick={onUpload}
        />
      </Cell>
    </Dialog>
  )
}

export default EducationModal
